/**
 * CR-6521 rework: the failure log for a part-failed bulk OTIF override.
 *
 * Mirrors the PO-Lines bulk-operation log the ticket names as the format to
 * copy (POLinesTab.tsx "Bulk Operation — Failed Lines"): the lines that could
 * not be processed, each with the server's own reason, while the table keeps
 * them selected for a retry. Before this, a partial failure reported only a
 * count and cleared the selection, so recovering meant re-scanning the table
 * by hand.
 *
 * The description states the retention RULE rather than a live count: the
 * selection is owned by OTIFReviewTab and is pruned asynchronously after this
 * dialog opens (a line that failed PERMANENTLY comes back from the refetch
 * ineligible, loses its checkbox, and is dropped), so any sentence describing
 * the live selection here can be stale by the time it is read. The rule is
 * scoped to THIS PAGE deliberately: the prune keeps only ids present and
 * eligible on the currently displayed page, so a failed line that concurrent
 * activity shifts onto another page is deselected even though it can still be
 * retried there. Within the page the rule holds on every path — while this
 * dialog is open the modal blocks every other writer of the selection, and
 * the prune retains exactly the still-eligible ids the page shows.
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';

export interface BulkResultEntry {
  lineId: number;
  poNumber: string;
  lineNumber: number;
  error: string;
}

interface OTIFBulkResultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entries: BulkResultEntry[];
}

export default function OTIFBulkResultDialog({
  open, onOpenChange, entries,
}: OTIFBulkResultDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Bulk Operation — Failed Lines</DialogTitle>
          <DialogDescription>
            {entries.length} line{entries.length !== 1 ? 's' : ''} could not be processed.
            {' Any line on this page that can still be retried is left selected.'}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background">
              <tr className="border-b">
                <th className="text-left px-3 py-2 font-medium">PO Number</th>
                <th className="text-left px-3 py-2 font-medium">Line</th>
                <th className="text-left px-3 py-2 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.lineId} className="border-b last:border-0">
                  <td className="px-3 py-2">{entry.poNumber}</td>
                  <td className="px-3 py-2">{entry.lineNumber}</td>
                  <td className="px-3 py-2 text-red-600">{entry.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
